import { useState } from 'react';
import type { Configuracion, EstadoAfiliacion, FacturacionApi } from '../api/types';
import { fecha } from '../validacion';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from './ui';

interface Props {
  api: FacturacionApi;
  config: Configuracion;
  onGuardado: (c: Configuracion) => void;
}

const ESTADOS: Record<EstadoAfiliacion, { texto: string; tono: 'ok' | 'pendiente' | 'error' | 'neutro' }> = {
  REQUESTED: { texto: 'Solicitud enviada a la DGI', tono: 'pendiente' },
  PENDING: { texto: 'La solicitud falló — reenviar', tono: 'error' },
  SUBSCRIBED: { texto: 'Afiliado — puede emitir', tono: 'ok' },
  UNSUBSCRIBED: { texto: 'No afiliado / dado de baja', tono: 'error' },
};

export function AfiliacionSeccion({ api, config, onGuardado }: Props) {
  const e = config.emisor;
  const [accion, setAccion] = useState<'afiliar' | 'qr' | 'qrManual' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [qr, setQr] = useState('');
  const [modoQr, setModoQr] = useState<'generar' | 'manual'>('generar');
  const [confirmarQr, setConfirmarQr] = useState(false);

  if (!e?.alanubeIdCompany) {
    return (
      <section className="seccion">
      <Guia
        id="afiliacion"
        titulo="Cómo completar la afiliación"
        pasos={[
          <>La solicitud de afiliación se envía sola a la DGI cuando registra su comercio.</>,
          <>Ingrese al portal <b>Etax</b> de la DGI con las credenciales del contribuyente, vaya a <b>Afiliación Contribuyente → Confirmación PAC</b> y acepte la solicitud del PAC.</>,
          <>Vuelva aquí y pulse <b>Ya acepté en Etax — validar</b>.</>,
          <>Genere el <b>código QR</b> o registre el que ya tiene en Etax.</>,
        ]}
        nota="Cuando los seis pasos de la barra superior estén en verde, sus ventas se facturarán electrónicamente de forma automática."
      />
        <header className="seccion__cab">
          <h2>Afiliación DGI y código QR</h2>
        </header>
        <Alerta tipo="info" titulo="Primero registre el emisor">
          La solicitud de afiliación a la DGI se envía automáticamente al registrar el comercio.
        </Alerta>
      </section>
    );
  }

  async function ejecutar(tipo: 'afiliar' | 'qr' | 'qrManual', fn: () => Promise<Configuracion>) {
    setAccion(tipo);
    setError(null);
    try {
      onGuardado(await fn());
      if (tipo === 'qrManual') setQr('');
      setConfirmarQr(false);
    } catch (err) {
      setError(err);
    } finally {
      setAccion(null);
    }
  }

  const estado = e.afiliacionEstado;
  const suscrito = estado === 'SUBSCRIBED';

  return (
    <section className="seccion">
      <Guia
        id="afiliacion"
        titulo="Cómo completar la afiliación"
        pasos={[
          <>La solicitud de afiliación se envía sola a la DGI cuando registra su comercio.</>,
          <>Ingrese al portal <b>Etax</b> de la DGI con las credenciales del contribuyente, vaya a <b>Afiliación Contribuyente → Confirmación PAC</b> y acepte la solicitud del PAC.</>,
          <>Vuelva aquí y pulse <b>Ya acepté en Etax — validar</b>.</>,
          <>Genere el <b>código QR</b> o registre el que ya tiene en Etax.</>,
        ]}
        nota="Cuando los seis pasos de la barra superior estén en verde, sus ventas se facturarán electrónicamente de forma automática."
      />
      <header className="seccion__cab">
        <div>
          <h2>Afiliación DGI y código QR</h2>
          <p className="texto-sec">
            Para emitir, la DGI debe aprobar al PAC del comercio y el comercio debe tener
            registrado su código de seguridad QR.
          </p>
        </div>
        {estado ? <Pill tono={ESTADOS[estado].tono}>{ESTADOS[estado].texto}</Pill> : <Pill tono="neutro">Sin estado</Pill>}
      </header>

      <ErrorApi error={error} />

      <ol className="pasos">
        <li className={estado && estado !== 'PENDING' ? 'hecho' : estado === 'PENDING' ? 'fallo' : ''}>
          <div className="pasos__titulo">Solicitud de afiliación enviada a la DGI</div>
          <p>Se envió automáticamente al registrar el comercio.</p>
          {estado === 'PENDING' && (
            <div className="pasos__accion">
              <Alerta tipo="aviso">El envío de la solicitud falló. Reintente.</Alerta>
              <Boton onClick={() => ejecutar('afiliar', () => api.afiliar(config.idEmpresa))} cargando={accion === 'afiliar'}>
                Reenviar solicitud
              </Boton>
            </div>
          )}
        </li>

        <li className={suscrito ? 'hecho' : estado === 'REQUESTED' ? 'actual' : ''}>
          <div className="pasos__titulo">Aceptar al PAC en Etax</div>
          <p>
            Ingrese al portal <b>Etax</b> de la DGI con las credenciales del contribuyente, vaya a{' '}
            <b>Afiliación Contribuyente → Confirmación PAC</b> y acepte la solicitud del PAC.
          </p>
        </li>

        <li className={suscrito ? 'hecho' : ''}>
          <div className="pasos__titulo">Validar la afiliación</div>
          <p>Después de aceptar en Etax, valide aquí. Si la DGI confirmó, el estado cambia a “Afiliado”.</p>
          {!suscrito && (
            <div className="pasos__accion">
              <Boton
                onClick={() => ejecutar('afiliar', () => api.afiliar(config.idEmpresa))}
                cargando={accion === 'afiliar'}
                deshabilitado={estado === 'PENDING'}
              >
                Ya acepté en Etax — validar
              </Boton>
            </div>
          )}
        </li>

        <li className={e.qrRegistrado ? 'hecho' : suscrito ? 'actual' : ''}>
          <div className="pasos__titulo">Código de seguridad QR</div>
          <p>
            Código privado entre el PAC y la DGI, ligado al RUC. Cada comprobante usa el último QR generado.
            {e.qrRegistrado && e.qrFechaRegistro && <> Registrado el {fecha(e.qrFechaRegistro)}.</>}
          </p>

          <div className="pasos__accion">
            <div className="segmentado" role="radiogroup" aria-label="Cómo registrar el QR">
              <label className={modoQr === 'generar' ? 'activo' : ''}>
                <input type="radio" checked={modoQr === 'generar'} onChange={() => setModoQr('generar')} />
                Generar nuevo
              </label>
              <label className={modoQr === 'manual' ? 'activo' : ''}>
                <input type="radio" checked={modoQr === 'manual'} onChange={() => setModoQr('manual')} />
                Ya tengo un QR de Etax
              </label>
            </div>

            {modoQr === 'generar' ? (
              confirmarQr ? (
                <Alerta tipo="aviso" titulo="¿Generar un nuevo código QR?">
                  El nuevo código reemplaza al anterior. Si el comercio también emite con otro proveedor (PAC), deberá
                  actualizar el QR allí.
                  <div className="acciones acciones--inline">
                    <Boton tipo="secundario" onClick={() => setConfirmarQr(false)}>
                      Cancelar
                    </Boton>
                    <Boton onClick={() => ejecutar('qr', () => api.generarQr(config.idEmpresa))} cargando={accion === 'qr'}>
                      Sí, generar
                    </Boton>
                  </div>
                </Alerta>
              ) : (
                <Boton tipo={e.qrRegistrado ? 'secundario' : 'primario'} onClick={() => setConfirmarQr(true)}>
                  {e.qrRegistrado ? 'Regenerar código QR' : 'Generar código QR'}
                </Boton>
              )
            ) : (
              <div className="qr-manual">
                <Campo etiqueta="Código QR obtenido en Etax">
                  {(id) => <textarea id={id} rows={2} className="mono" value={qr} onChange={(ev) => setQr(ev.target.value)} />}
                </Campo>
                <Boton
                  onClick={() => ejecutar('qrManual', () => api.registrarQr(config.idEmpresa, qr))}
                  cargando={accion === 'qrManual'}
                  deshabilitado={!qr.trim()}
                >
                  Registrar QR
                </Boton>
              </div>
            )}
          </div>
        </li>
      </ol>

      {estado === 'UNSUBSCRIBED' && (
        <Alerta tipo="error" titulo="El comercio no está afiliado">
          La DGI no tiene vigente la afiliación con el PAC. Ingrese a Etax y revise la confirmación PAC; luego pulse validar de nuevo.
        </Alerta>
      )}
      {e.alanubeActiva === false && (
        <Alerta tipo="error" titulo="La emisión del comercio está inactiva">
          La causa más común es un código QR inválido. Registre un QR vigente arriba y la emisión se reactiva.
        </Alerta>
      )}
    </section>
  );
}
